import React, { useRef, useCallback, useState, useEffect, memo } from 'react';
import styled from 'styled-components';
import { TranscriptElement as ITranscriptElement, TextElement } from '../models/TranscriptElement';
import { defaultTheme } from './theme';
import { TranscriptElementProps } from './interfaces';

/**
 * Type guard to check if an element is a text element
 */
const isTextElement = (element: ITranscriptElement): element is TextElement => {
  return element.type === 'text';
};

/**
 * Component for rendering a single transcript element (word or punctuation)
 * Uses memoization to prevent unnecessary re-renders
 */
const TranscriptElement: React.FC<TranscriptElementProps> = memo(({
  element,
  segmentId,
  paragraphId,
  index,
  isLastInParagraph,
  isFollowedByPunctuation,
  onTextChange,
  onClick,
  onKeyDown,
  searchTerm = '',
  counter
}) => {
  // Reference to the DOM element for direct manipulation
  const elementRef = useRef<HTMLSpanElement>(null);
  
  // Local state for tracking if we're currently editing
  const [isEditing, setIsEditing] = useState(false);
  
  // Handle blurring (losing focus) from the element
  const handleBlur = useCallback(() => {
    if (!elementRef.current) return;
    
    const newText = elementRef.current.textContent || '';
    onTextChange(element.id, newText, true);
    setIsEditing(false);
  }, [element.id, onTextChange]);
  
  // Handle input events while typing
  const handleInput = useCallback((e: React.FormEvent<HTMLSpanElement>) => {
    if (!elementRef.current) return;
    
    const newText = elementRef.current.textContent || '';
    
    // Only call onTextChange if we actually changed the text
    if (newText !== element.value) {
      onTextChange(element.id, newText, false);
      setIsEditing(true);
    }
  }, [element.id, element.value, onTextChange]);
  
  // Handle click events
  const handleClick = useCallback((e: React.MouseEvent<HTMLSpanElement>) => {
    // First, focus if not already focused
    if (e.currentTarget !== document.activeElement) {
      e.currentTarget.focus();
    }
    
    // If we have a click handler, call it with the relevant info
    if (onClick) {
      // Use type guard to safely access timing properties
      const startTime = isTextElement(element) ? element.startTime : undefined;
      onClick(e, element.id, e.currentTarget, startTime);
    }
  }, [element, onClick]);
  
  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLSpanElement>) => {
    // Forward to the parent handler if provided
    if (onKeyDown) {
      onKeyDown(e, element.id);
    }
  }, [element.id, onKeyDown]);
  
  // Use useEffect to set initial focus if element is created fresh
  useEffect(() => {
    // Check if this is a new empty element that needs focus
    if (element.value === '' && elementRef.current) {
      elementRef.current.focus();
    }
  }, [element.value]);
  
  // Determine CSS classes based on element type and position
  const lowerSearch = searchTerm.trim().toLowerCase();
  const isMatch = lowerSearch.length >= 1 && element.value.toLowerCase().includes(lowerSearch);
  const cssClasses = `transcript-element ${element.type} ${isMatch ? 'search-match' : ''}`;
  
  // Get timing info for text elements using type guard
  const startTime = isTextElement(element) ? element.startTime : undefined;
  const endTime = isTextElement(element) ? element.endTime : undefined;

  // Render the element as an editable span
  return (
    <ElementSpan
      ref={elementRef}
      contentEditable={true}
      suppressContentEditableWarning={true}
      className={cssClasses}
      data-element-id={element.id}
      data-element-type={element.type}
      data-segment-id={segmentId}
      data-paragraph-id={paragraphId}
      data-start-time={startTime}
      data-end-time={endTime}
      data-is-word={element.type === 'text' ? 'true' : 'false'}
      data-is-last={isLastInParagraph ? 'true' : 'false'}
      data-followed-by-punct={isFollowedByPunctuation ? 'true' : 'false'}
      data-element-color={element.color}
      onBlur={handleBlur}
      onInput={handleInput}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      $elementColor={element.color}
    >
      {element.value}
    </ElementSpan>
  );
}, (prevProps, nextProps) => {
  if (prevProps.counter !== nextProps.counter) return false;
  if (prevProps.element !== nextProps.element) return false;
  if (prevProps.element.counter !== nextProps.element.counter) return false;
  if (prevProps.element.value !== nextProps.element.value) return false;
  if (prevProps.element.color !== nextProps.element.color) return false;
  if (prevProps.segmentId !== nextProps.segmentId) return false;
  if (prevProps.paragraphId !== nextProps.paragraphId) return false;
  if (prevProps.index !== nextProps.index) return false;
  if (prevProps.searchTerm !== nextProps.searchTerm) return false;
  return true;
});

TranscriptElement.displayName = 'TranscriptElement';

export default TranscriptElement;


/**
 * Span for individual transcript elements (words and punctuation)
 */
export const ElementSpan = styled.span<{ $elementColor?: string }>`
  outline: none;
  display: inline;
  position: relative;
  min-width: 1px; /* Ensures empty spans are still editable */
  cursor: text;
  user-select: text; /* Crucial for proper selection behavior */
  transition: background-color 0.1s ease;
  border-radius: ${defaultTheme.borderRadius.sm};
  
  /* Improve text appearance */
  padding: 0;
  margin: 0;
  
  /* Apply automatic spacing between words - cross-browser compatible version */
  /* Base spacing for all text elements */
  &.text:not(:last-child) {
    margin-right: 0.25em;
  }
  /* Remove margin before punctuation for all browsers */
  &.text + .punct {
    margin-left: -0.25em;
  }

  /* Color highlight */
  ${props => props.$elementColor && `
    background-color: ${props.$elementColor}9E;
    border-radius: 0px;
  `}

  /* Match highlight */
  &.search-match {
    background-color: rgba(255, 243, 128, 0.45);
    border-radius: 0px;
  }
  
  /* Very subtle hover state */
  &:hover {
    /* Extremely subtle hover for text */
    background-color: rgba(242, 208, 147, 0.05);
  }
  
  /* Light focus state - almost invisible */
  &:focus {
    background-color: rgba(255, 255, 255, 0.03);
    outline: none;
  }
  
  /* Enhanced active time marker */
  &.active-time, &.active-word {
    box-shadow: 0 2px 0 rgba(242, 208, 147, 0.9);
    border-radius: 3px;
    background-color: rgba(242, 208, 147, 0.15);
    font-weight: 600;
    padding: 0 3px;
    margin: 0 1px;
    position: relative;
    z-index: 2;
  }
  
  /* Google Docs-like selection state */
  &.selected {
    background-color: ${defaultTheme.colors.selection} !important;
    color: ${defaultTheme.colors.text};
    border-radius: 0px;
    position: relative;
    z-index: 1;
    box-shadow: 0 0 0 1px rgba(76, 154, 255, 0.2);
  }
  
  /* First selected element gets a left rounded edge */
  &.selected:first-child,
  &.selected:first-of-type {
    border-top-left-radius: ${defaultTheme.borderRadius.sm};
    border-bottom-left-radius: ${defaultTheme.borderRadius.sm};
  }
  
  /* Last selected element gets a right rounded edge */
  &.selected:last-child,
  &.selected:last-of-type {
    border-top-right-radius: ${defaultTheme.borderRadius.sm};
    border-bottom-right-radius: ${defaultTheme.borderRadius.sm};
  }
  
  /* Note: The :has() selector was replaced with :first-of-type and :last-of-type
     for better cross-browser compatibility. */
  
  /* Highlight effect when scrolling to colored element */
  &.highlight-element {
    animation: highlightPulse 2s ease-out;
  }
  
  @keyframes highlightPulse {
    0% {
      box-shadow: 0 0 0 0 rgba(242, 208, 147, 0.8);
      transform: scale(1);
    }
    20% {
      box-shadow: 0 0 0 4px rgba(242, 208, 147, 0.4);
      transform: scale(1.05);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(242, 208, 147, 0);
      transform: scale(1);
    }
  }
  
  /* Style for punctuation */
  &.punct {
    /* No space around punctuation */
    margin-left: 0;
    margin-right: 0;
  }
  
  /* Fix selection appearance for text and selections */
  ::selection {
    background-color: transparent !important;
    color: ${defaultTheme.colors.text};
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
  }
`;